import React from 'react'
import { OptimizelyExperiments } from 'constants/optimizely'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  arePropertyRatesLoading,
  getPropertyCheapestRates,
  getPropertyRatesKey,
} from 'selectors/offerPage/offerPageSelectors'
import useCheapestPropertyPricing from 'hooks/OfferPage/useCheapestPropertyPricing'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import _BaseHotelFlightsToggleButton from './_BaseHotelFlightsToggleButton'
import { getSppPrices } from './utils'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

interface Props {
  offer: App.HotelOffer
  areAnytimeRates: boolean
  isLoading: boolean
  cheapestPropertyRates: [App.PropertyRate | undefined, App.PropertyRate | undefined]
}

function SinglePropertyHotelFlightsToggleButton(props: Props) {
  const {
    offer,
    areAnytimeRates,
    isLoading,
    cheapestPropertyRates,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const [hotelOnlyRate, hotelWithFlightsRate] = cheapestPropertyRates
  const [
    pricing,
    memberPricing,
    pricingWithFlights,
    memberPricingWithFlights,
  ] = useCheapestPropertyPricing()

  const shouldHideAnytimePrices = !!useOptimizelyExperiment(OptimizelyExperiments.sppHideAnytimePrices)
  const hideAnytimePrices = areAnytimeRates && shouldHideAnytimePrices
  const [hotelPrice, hotelMemberPrice, hotelFlightPrice] = hideAnytimePrices ? [0, 0, 0] : getSppPrices({
    hotelOnlyRate,
    hotelWithFlightsRate,
    pricing,
    memberPricing,
    pricingWithFlights,
    memberPricingWithFlights,
  })

  // Only show LuxPlus+ badging if the member price is _better_ than the non-member price.
  const showMemberPrice = !!hotelMemberPrice && hotelMemberPrice < hotelPrice
  const hotelTotalPrice = showMemberPrice ? hotelMemberPrice : hotelPrice

  // TODO: Checking these LPC offer flags isn't appropriate if we're dealing with an LPP rate.
  const flightsAvailable = offer.offerFlightsEnabled && !offer.flightCacheDisabled

  return <_BaseHotelFlightsToggleButton
    flightsAvailable={flightsAvailable}
    isMemberPrice={showMemberPrice}
    hotelTotalPrice={hotelTotalPrice}
    hotelFlightTotalPrice={hotelTotalPrice !== hotelFlightPrice ? hotelFlightPrice : undefined}
    pricesLoading={isLoading}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer as App.HotelOffer,
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  open: !!state.activeFilter,
}), SinglePropertyHotelFlightsToggleButton, preventRenderWithOpenFilter)
